import { Eye, Globe, MoreHorizontal, Power, PowerOff, Shield } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { ApiKeyRow } from "../hooks/useApiKeysCatalog";

export interface ApiKeyRowActionsProps {
  row: ApiKeyRow;
  /**
   * False when the roles drawer would have nothing truthful to show — the catalog 403'd, or THIS key's roles
   * fetch failed. Opening it then would render "0 assigned" with every box clear, indistinguishable from a
   * confirmed "holds nothing".
   */
  canOpenRoles: boolean;
  canUpdate: boolean;
  canManageRoles: boolean;
  onManageRoles: (row: ApiKeyRow) => void;
  onEditIps: (row: ApiKeyRow) => void;
  onDisable: (row: ApiKeyRow) => void;
  onEnable: (row: ApiKeyRow) => void;
  /** Cards push the menu to the far edge; the table keeps both controls together on the right. */
  spread?: boolean;
}

/**
 * Everything you can do to a key from a row: open its roles, and the lifecycle menu.
 *
 * Shared by the table and the cards. It used to be copied into both, which is how a new action ends up on
 * desktop and silently missing on mobile.
 *
 * The lifecycle menu deliberately survives a roles-catalog 403 — editing IPs and enabling or disabling do not
 * depend on it, and taking them away because a different endpoint failed would be a worse answer than showing
 * the access column as unknown.
 */
export function ApiKeyRowActions({
  row,
  canOpenRoles,
  canUpdate,
  canManageRoles,
  onManageRoles,
  onEditIps,
  onDisable,
  onEnable,
  spread = false,
}: ApiKeyRowActionsProps) {
  const { t } = useTranslations("settings");

  return (
    <div className={cn("flex items-center gap-1.5", !spread && "justify-end")}>
      {canOpenRoles && (
        <ColocaButton
          variant="default"
          size="sm"
          icon={canManageRoles ? Shield : Eye}
          onClick={() => onManageRoles(row)}
        >
          {canManageRoles ? t("access_action_roles") : t("access_action_view")}
        </ColocaButton>
      )}
      {canUpdate && (
        <div className={spread ? "ml-auto" : undefined}>
          <ColocaDropdownMenu
            align="end"
            trigger={
              <ColocaIconButton
                icon={MoreHorizontal}
                variant="default"
                size="sm"
                aria-label={t("access_apikeys_more_actions")}
              />
            }
            items={[
              {
                label: t("api_keys_edit_ips_button"),
                icon: Globe,
                onClick: () => onEditIps(row),
              },
              row.key.is_active
                ? {
                    label: t("api_keys_disable_action"),
                    icon: PowerOff,
                    variant: "destructive" as const,
                    onClick: () => onDisable(row),
                  }
                : {
                    label: t("api_keys_enable_action"),
                    icon: Power,
                    onClick: () => onEnable(row),
                  },
            ]}
          />
        </div>
      )}
    </div>
  );
}
